import type React from "react";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

export const skeletonStyle = tv({
  base: "animate-pulse rounded-full bg-gray-200",
});

export const TableSkeleton: React.FC = () => {
  // Use a high colspan to ensure the skeleton takes up the full width of the table
  return (
    <tr>
      <td colSpan={100}>
        <TableSkeletonContent />
      </td>
    </tr>
  );
};

export const TableSkeletonContent: FC<{
  className?: string;
  rowCount?: number;
}> = ({ className, rowCount = 3 }) => {
  return (
    <div
      className={twMerge("animate-pulse divide-y divide-gray-200", className)}
      role="status"
    >
      {Array.from({ length: rowCount }).map((_, index) => (
        <div key={index} className="flex items-center justify-between p-4">
          <div>
            <div className="mb-2.5 h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-400" />
            <div className="h-2 w-32 rounded-full bg-gray-200 dark:bg-gray-400" />
          </div>
          <div className="h-2.5 w-12 rounded-full bg-gray-300 dark:bg-gray-400" />
        </div>
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  );
};

export const DefaultSkeleton: React.FC = () => {
  return (
    <div className="max-w-sm animate-pulse" role="status">
      <div className="mb-4 h-2.5 w-48 rounded-full bg-gray-200 dark:bg-gray-400" />
      <div className="mb-2.5 h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-400" />
      <div className="mb-2.5 h-2 rounded-full bg-gray-200 dark:bg-gray-400" />
      <div className="mb-2.5 h-2 max-w-[330px] rounded-full bg-gray-200 dark:bg-gray-400" />
      <div className="mb-2.5 h-2 max-w-[300px] rounded-full bg-gray-200 dark:bg-gray-400" />
      <div className="h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-400" />
      <span className="sr-only">Loading...</span>
    </div>
  );
};

export const TextSkeleton: React.FC = () => {
  return (
    <div className="max-w-lg animate-pulse space-y-2.5" role="status">
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-32 rounded-full bg-gray-200 dark:bg-gray-400" />
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-400" />
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-400" />
      </div>
      <div className="flex w-full max-w-[480px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-400" />
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-400" />
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-400" />
      </div>
      <div className="flex w-full max-w-[400px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-400" />
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-400" />
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-400" />
      </div>
      <div className="flex w-full max-w-[480px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-400" />
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-400" />
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-400" />
      </div>
      <div className="flex w-full max-w-[440px] items-center space-x-2">
        <div className="h-2.5 w-32 rounded-full bg-gray-300 dark:bg-gray-400" />
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-400" />
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-400" />
      </div>
      <div className="flex w-full max-w-[360px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-400" />
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-400" />
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-400" />
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

export const ChartSkeleton: React.FC<{ columnCount?: number }> = ({
  columnCount = 9,
}) => {
  // Generate varying heights for bars to simulate a distribution
  const heights = Array.from({ length: columnCount }, (_, i) => {
    // Generate height based on a sine wave pattern for natural variation
    const normalized = i / (columnCount - 1); // 0 to 1
    const wave = Math.sin(normalized * Math.PI); // Creates a curve from 0 to 1 to 0
    return Math.round(20 + wave * 60); // Heights between 20% and 80%
  });

  return (
    <div className="flex h-full w-full items-end gap-1" role="status">
      {heights.map((height, index) => (
        <div
          key={index}
          className={`w-full animate-pulse rounded-t ${
            index % 2 === 0 ? "bg-gray-100" : "bg-gray-200"
          } dark:bg-gray-300`}
          style={{ height: `${height}%` }}
        />
      ))}
      <span className="sr-only">Loading chart...</span>
    </div>
  );
};

export const FullPageSkeleton = (): ReactNode => {
  return (
    <div className="mx-auto max-w-4xl space-y-11 p-6">
      <DefaultSkeleton />
      <TextSkeleton />
    </div>
  );
};
